import React from "react";
import { Box, useColorModeValue } from "@chakra-ui/react";

// ─────────────────────────────────────────────────────────────
// ORIGINAL — pure wordmark (600 weight, 0.35em tracking)
// To restore: change NavBar import to { LogoOriginal as Logo }
// ─────────────────────────────────────────────────────────────
export function LogoOriginal() {
  const nameColor = useColorModeValue("#0c0c0c", "#f2f2f2");
  const ruleColor = useColorModeValue("#c4c4c4", "#383838");
  const subColor  = useColorModeValue("#707070", "#909090");

  return (
    <Box
      as="span"
      display="inline-flex"
      flexDirection="column"
      alignItems="flex-start"
      userSelect="none"
      aria-label="Huy Phu"
      sx={{
        "@keyframes logoReveal": {
          "0%":   { opacity: 0, transform: "translateY(7px)" },
          "100%": { opacity: 1, transform: "translateY(0)"   },
        },
        animation: "logoReveal 0.65s cubic-bezier(0.16, 1, 0.3, 1) both",
      }}
    >
      {/* Name — Cinzel brings classical, authoritative weight */}
      <Box
        as="span"
        fontFamily="'Cinzel', serif"
        fontSize="27px"
        fontWeight="500"
        letterSpacing="0.28em"
        color={nameColor}
        lineHeight={1}
        textTransform="uppercase"
        pb="10px"
      >
        Huy Phu
      </Box>

      {/* Thin rule — separates name from descriptor */}
      <Box w="100%" h="1px" bg={ruleColor} />

      {/* Descriptor — Josefin Sans keeps the geometric/technical feel */}
      <Box
        as="span"
        w="100%"
        textAlign="center"
        fontFamily="'Josefin Sans', sans-serif"
        fontSize="9.5px"
        fontWeight="400"
        letterSpacing="0.32em"
        color={subColor}
        lineHeight={1}
        textTransform="uppercase"
        pt="8px"
      >
        Security · QA
      </Box>
    </Box>
  );
}

// ─────────────────────────────────────────────────────────────
// ACTIVE — HP hallmark seal + editorial wordmark
// Filled badge, inverts between light/dark, spring-scale on hover
// ─────────────────────────────────────────────────────────────
export default function Logo() {
  const badgeBg    = useColorModeValue("#111111", "#ececec");
  const badgeFg    = useColorModeValue("#f0f0f0", "#111111");
  const divColor   = useColorModeValue("#d0d0d0", "#3a3a3a");
  const nameColor  = useColorModeValue("#111111", "#ececec");
  const subColor   = useColorModeValue("#888888", "#777777");

  return (
    <Box
      as="span"
      display="inline-flex"
      alignItems="center"
      gap="22px"
      userSelect="none"
      aria-label="Huy Phu"
      sx={{
        "& .hp-mark": {
          transition: "transform 0.35s cubic-bezier(0.34, 1.56, 0.64, 1)",
        },
        "&:hover .hp-mark": {
          transform: "scale(1.06)",
        },
      }}
    >
      {/* ── Filled hallmark badge ── */}
      <svg
        className="hp-mark"
        viewBox="0 0 56 56"
        height="62"
        width="62"
        xmlns="http://www.w3.org/2000/svg"
        aria-hidden="true"
        style={{ display: "block", flexShrink: 0 }}
      >
        {/* Solid badge background */}
        <rect x="0" y="0" width="56" height="56" fill={badgeBg} />

        {/* HP lettermark — H and P share the center vertical stroke */}
        <g
          stroke={badgeFg}
          strokeWidth="2.6"
          strokeLinecap="square"
          strokeLinejoin="miter"
          fill="none"
        >
          {/* H — left vertical */}
          <line x1="11" y1="11" x2="11" y2="45" />
          {/* H — crossbar, stops at shared stroke */}
          <line x1="11" y1="28" x2="29" y2="28" />
          {/* H / P — shared center vertical */}
          <line x1="29" y1="11" x2="29" y2="45" />
          {/* P — bowl: two joined quadratic Béziers, clean right-angle turn */}
          <path d="M29,11 Q46,11 46,19.5 Q46,28 29,28" />
        </g>
      </svg>

      {/* ── 1px vertical divider ── */}
      <Box
        as="span"
        display="inline-block"
        w="1px"
        h="48px"
        bg={divColor}
        flexShrink={0}
      />

      {/* ── Wordmark — stacked two-line ── */}
      <Box
        as="span"
        display="inline-flex"
        flexDirection="column"
        alignItems="flex-start"
        gap="8px"
      >
        {/* HUY PHU — 600 weight, airy editorial tracking */}
        <Box
          as="span"
          fontFamily="'Josefin Sans', sans-serif"
          fontSize="22px"
          fontWeight="600"
          letterSpacing="0.35em"
          color={nameColor}
          lineHeight={1}
          textTransform="uppercase"
        >
          huy phu
        </Box>

        {/* security · qa — light descriptor */}
        <Box
          as="span"
          fontFamily="'Josefin Sans', sans-serif"
          fontSize="11px"
          fontWeight="400"
          letterSpacing="0.2em"
          color={subColor}
          lineHeight={1}
          textTransform="uppercase"
        >
          security · qa
        </Box>
      </Box>
    </Box>
  );
}
